import { z } from "zod";
import { IdSchema, IsoDateOrDateTimeSchema, IsoDateTimeSchema } from "./common.js";
import { DEVICE_LOCATIONS } from "../constants/index.js";

/**
 * 진실의 원천: harness/API-SPEC.md §3, apps/api/migrations/0001_init.sql (devices)
 */

export const DeviceSchema = z.object({
  serial_norm: z.string().min(1),
  serial_raw: z.string().min(1),
  model: z.string(),
  owner_company_id: IdSchema.nullable(),
  location: z.enum(DEVICE_LOCATIONS).nullable(),
  // 처음/마지막 관측은 입고일에서 오는 경우가 많아 날짜만 아는 값이다
  first_seen_at: IsoDateOrDateTimeSchema.nullable(),
  last_seen_at: IsoDateOrDateTimeSchema.nullable(),
  is_spare: z.boolean(),
  firmware: z.string().nullable(),
});
export type Device = z.infer<typeof DeviceSchema>;

// 시리얼 하나로 전 생애 조회 (harness/UI-SPEC.md §1 "장비" 화면)
//
// 개인정보(담당자·연락처)는 이 응답에 절대 싣지 않는다 — 타임라인은 "무슨 일이
// 있었는가"만 말한다 (harness/API-SPEC.md §3, H5 ⑤).
export const DeviceTimelineEventSchema = z.object({
  // 언제인지 모르는 사건이 실제로 있다(수집 원문에 교체일이 비어 있는 경우).
  // 그때 날짜를 지어내지 않고 null 로 둔다 (harness/SOUL.md S2).
  at: IsoDateOrDateTimeSchema.nullable(),
  kind: z.enum([
    "CASE_RECEIVED",
    "STATUS_CHANGE",
    "SWAP",
    "SHIPMENT",
    "NOTE",
    "EVIDENCE",
  ]),
  case_id: IdSchema.nullable(),
  summary: z.string(),
  ref_id: IdSchema.nullable(),
  /**
   * ★ 이 사건이 잇고 있는 **다른 시리얼**. 교체(SWAP)에만 있다.
   *
   * 요약 문장 안에 시리얼이 글자로 들어 있어도 사람은 그것을 **누를 수 없다.**
   * 손에 든 것이 교체품일 때 원 시리얼로 건너갈 방법이 화면에 없으면,
   * 데이터에 남아 있어도 그 사람에게는 연결이 끊긴 것이다(`SOUL.md` P1).
   */
  related_serial: z.string().nullable(),
});
export type DeviceTimelineEvent = z.infer<typeof DeviceTimelineEventSchema>;

export const DeviceTimelineSchema = z.object({
  device: DeviceSchema,
  events: z.array(DeviceTimelineEventSchema),
});
export type DeviceTimeline = z.infer<typeof DeviceTimelineSchema>;

/**
 * 장비 메모. **증적이 아니다** — 대표 지시·통화 내용처럼 케이스가 아니라
 * 장비에 남는 사실을 적는다(harness/API-SPEC.md §3). 케이스가 끝나도 남고,
 * 지우는 경로는 두지 않는다.
 */
export const DeviceNoteSchema = z.object({
  id: IdSchema,
  serial_norm: z.string().min(1),
  category: z.string().nullable(),
  body: z.string().min(1),
  author_id: IdSchema.nullable(),
  created_at: IsoDateTimeSchema,
});
export type DeviceNote = z.infer<typeof DeviceNoteSchema>;

export const DeviceNoteCreateRequestSchema = z.object({
  category: z.string().trim().min(1).max(40).optional(),
  body: z.string().trim().min(1),
});
export type DeviceNoteCreateRequest = z.infer<typeof DeviceNoteCreateRequestSchema>;
